// --- External Dependencies
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- UI Components
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import { InputLabel } from '../../../../components/InputLabel/InputLabel';
import { Col, Row } from 'antd';
// --- Styles
import './styles.scss';
// --- Hooks
import { useLoading } from '../../../../utils/hooks/useLoading';
import { useTagsActions } from '../../../../store/tags/useTags';
// --- Utilities
import { filterCriteria } from '../../../../utils/filterCriteria';
// --- API
import { datastageApi } from '../../../../api';

/**
 * TagsFilterForm Component
 * Provides a filter form for filtering tags with options to reset or apply filters.
 *
 * @returns {JSX.Element} The TagsFilterForm component.
 */
export function TagsFilterForm() {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { updateTags, updateTagsFilterForm, clearTags } = useTagsActions();
    const { filterForm, defaultFilterRange, tagSort } = useSelector((s) => s.tags);

    // Automatically apply the filter on component mount
    useEffect(() => {
        setTimeout(() => {
            handleFilter();
        }, 5);
    }, []);

    // Fetch filtered tags data from the API and updates the store
    const handleFilter = async () => {
        try {
            setLoadingTrue();

            const data = await datastageApi.tags(
                filterCriteria(filterForm, '', 'tags'),
                defaultFilterRange,
                tagSort,
            );

            updateTags({
                tags: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            console.error('Error filtering tags:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Clear the filter form and fetches all tags data from the API.
    const handleResetFilter = async () => {
        try {
            setLoadingTrue();

            clearTags();

            const data = await datastageApi.tags({}, defaultFilterRange, tagSort);

            updateTags({
                tags: data?.content,
                totalPages: data?.totalPages,
                currentPage: data?.pageable?.pageNumber,
                totalItems: data?.totalElements,
            });
        } catch (error) {
            console.error('Error resetting tag filter:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <Row className="tags-filter-form">
            {/* Input Fields for Filtering */}
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Tag Name">
                    <Input
                        value={filterForm.name}
                        onChange={(e) =>
                            updateTagsFilterForm({
                                key: 'filterForm',
                                data: { name: e.target.value },
                            })
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFilter();
                            }
                        }}
                        width="auto"
                        id="name"
                        name="name"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            {/* Action Buttons for Filter and Reset */}
            <Col>
                <Button
                    className="filter-button"
                    sizeType="small"
                    styleType="promoted"
                    onClick={handleFilter}
                >
                    Filter
                </Button>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
